import type {
  EnforcementEnvironment,
  EnforcementReadiness,
  EnforcementReadinessCounts,
  EnforcementReadinessStatus,
} from "@/api/temp/AgentEstateService";

/**
 * TrustLogix enforcement readiness, as every surface reads it. Pure - no React,
 * no i18n - so the board, the cards, the list, the wizard and the specs all
 * derive the same answer from the same counts.
 *
 * Two rules hold everywhere:
 *  - missing readiness is UNKNOWN, never SETUP_REQUIRED (an unread answer is
 *    not a negative one) and never READY;
 *  - an empty scope has no readiness at all - "0 of 0 ready" is never shown.
 */

export const agentReadinessStatus = (
  readiness?: Pick<EnforcementReadiness, "status">,
): EnforcementReadinessStatus => readiness?.status ?? "UNKNOWN";

export const emptyReadinessCounts = (): EnforcementReadinessCounts => ({
  ready: 0,
  setupRequired: 0,
  attentionRequired: 0,
  unknown: 0,
});

const COUNT_FIELD: Record<
  EnforcementReadinessStatus,
  keyof EnforcementReadinessCounts
> = {
  READY: "ready",
  SETUP_REQUIRED: "setupRequired",
  ATTENTION_REQUIRED: "attentionRequired",
  UNKNOWN: "unknown",
};

/** Adds one agent's status to a running tally, in place. */
export const tallyReadiness = (
  counts: EnforcementReadinessCounts,
  status: EnforcementReadinessStatus,
): void => {
  counts[COUNT_FIELD[status]] += 1;
};

export const readinessTotal = (counts: EnforcementReadinessCounts): number =>
  counts.ready +
  counts.setupRequired +
  counts.attentionRequired +
  counts.unknown;

export type ReadinessExtent = "full" | "partial" | "none" | "unavailable";

export interface ReadinessCoverage {
  ready: number;
  total: number;
  /** Every agent that is not READY - unknown included. */
  notReady: number;
  /** `unavailable` when there are no agents, or no counts could be read. */
  extent: ReadinessExtent;
}

export const readinessCoverage = (
  counts?: EnforcementReadinessCounts,
): ReadinessCoverage => {
  if (!counts)
    return { ready: 0, total: 0, notReady: 0, extent: "unavailable" };
  const total = readinessTotal(counts);
  const notReady = total - counts.ready;
  const extent: ReadinessExtent =
    total === 0
      ? "unavailable"
      : counts.ready === total
        ? "full"
        : counts.ready === 0
          ? "none"
          : "partial";
  return { ready: counts.ready, total, notReady, extent };
};

/**
 * What the estate-wide readiness signal (an empty estate, onboarding completion)
 * should say.
 *  - NO_AGENTS: nothing discovered yet, so there is nothing to claim.
 *  - ENVIRONMENT_REQUIRED: no saved enforcement strategy exists.
 *  - INCOMPLETE / COMPLETE: some / every discovered agent is ready.
 *  - UNKNOWN: the counts or the environment could not be read.
 */
export type EstateEnforcementState =
  | "NO_AGENTS"
  | "ENVIRONMENT_REQUIRED"
  | "ATTENTION_REQUIRED"
  | "INCOMPLETE"
  | "COMPLETE"
  | "UNKNOWN";

export const estateEnforcementState = (
  environment: EnforcementEnvironment | undefined,
  counts: EnforcementReadinessCounts | undefined,
  discoveredAgents: number,
): EstateEnforcementState => {
  if (discoveredAgents <= 0) return "NO_AGENTS";
  if (!environment || environment.status === "UNKNOWN") return "UNKNOWN";
  if (!environment.enabled) return "ENVIRONMENT_REQUIRED";
  if (environment.status === "ATTENTION_REQUIRED") return "ATTENTION_REQUIRED";
  const coverage = readinessCoverage(counts);
  if (coverage.extent === "unavailable") return "UNKNOWN";
  return coverage.extent === "full" ? "COMPLETE" : "INCOMPLETE";
};

/**
 * Whether a direct connection that bypasses TrustLogix is *known*. Only an
 * observed route qualifies - an unknown one must never be drawn as a bypass.
 */
export const bypassKnown = (readiness?: EnforcementReadiness): boolean =>
  Boolean(
    readiness &&
      readiness.status !== "UNKNOWN" &&
      readiness.reason !== "noEnvironment" &&
      !readiness.agentRoutedThroughEnforcement &&
      readiness.routingObserved,
  );

/**
 * Whether the agent's traffic is known to be routed through a TrustLogix
 * enforcement path. This is a routing fact only: an agent can be routed and
 * still not READY (an MCP connection it needs is missing or failing).
 */
export const routedThroughEnforcement = (
  readiness?: EnforcementReadiness,
): boolean =>
  Boolean(
    readiness &&
      readiness.agentRoutedThroughEnforcement &&
      readiness.status !== "UNKNOWN" &&
      readiness.reason !== "noEnvironment",
  );

/** `SETUP_REQUIRED` → `setupRequired`, for i18n keys. */
export const readinessKey = (status: EnforcementReadinessStatus): string =>
  status
    .toLowerCase()
    .replace(/_(\w)/g, (_match, letter: string) => letter.toUpperCase());

/**
 * `SETUP_REQUIRED` → `setup-required`, for class modifiers and test ids. Any
 * upper-snake id reads the same way (stage ids, connectivity statuses).
 */
export const readinessSlug = (status: string): string =>
  status.toLowerCase().replace(/_/g, "-");

/**
 * One status for a scope of agents - what a policy review or result reports
 * for "the agents these policies apply to". READY only when every agent is;
 * an empty or unreadable scope is UNKNOWN, never READY.
 */
export const scopeReadinessStatus = (
  counts?: EnforcementReadinessCounts,
): EnforcementReadinessStatus => {
  const coverage = readinessCoverage(counts);
  if (!counts || coverage.extent === "unavailable") return "UNKNOWN";
  if (coverage.extent === "full") return "READY";
  if (counts.setupRequired > 0) return "SETUP_REQUIRED";
  if (counts.attentionRequired > 0) return "ATTENTION_REQUIRED";
  return "UNKNOWN";
};
